import type {
  InspectAssessmentPeriodPrices,
  InspectAssessmentPricePoint,
} from '../../../../state/app/inspect_assessment/types';

export type AssessmentChartPoint = { x: string; y: number | null };

export const finiteOrNull = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const pmChartPrice = (
  point: InspectAssessmentPricePoint,
  isFxForward: boolean,
) =>
  finiteOrNull(
    isFxForward
      ? (point.fwd_ask ?? point.ask ?? point.close)
      : (point.ask ?? point.close),
  );

export const formatPrice = (value: number) =>
  value.toLocaleString('en', {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  });

export const formatYield = (value: number) =>
  `${value.toLocaleString('en', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  })}%`;

export const relevantPeriodPriceSeries = (
  data?: InspectAssessmentPeriodPrices,
) => {
  const boundaryAssetIds = new Set(
    [data?.start_underlying_asset_id, data?.end_underlying_asset_id].filter(
      (id): id is number => typeof id === 'number',
    ),
  );
  return boundaryAssetIds.size > 0
    ? (data?.series.filter(asset =>
        boundaryAssetIds.has(asset.underlying_asset_id),
      ) ?? [])
    : (data?.series ?? []);
};

const chronologicalValues = (points: AssessmentChartPoint[]) =>
  points
    .map((point, order) => ({
      order,
      timestamp: Date.parse(point.x),
      value: point.y,
    }))
    .filter(
      (point): point is { order: number; timestamp: number; value: number } =>
        point.value !== null && Number.isFinite(point.timestamp),
    )
    .sort(
      (left, right) =>
        left.timestamp - right.timestamp || left.order - right.order,
    );

const boundaryValues = (
  startPoints: AssessmentChartPoint[] | undefined,
  endPoints: AssessmentChartPoint[] | undefined,
) => {
  const starts = chronologicalValues(startPoints ?? []);
  const ends = chronologicalValues(endPoints ?? []);
  const start = starts[0];
  const end = ends[ends.length - 1];
  if (!start || !end || end.timestamp <= start.timestamp) return undefined;
  return { start: start.value, end: end.value };
};

export const assessmentPeriodMarketChanges = (
  data?: InspectAssessmentPeriodPrices,
) => {
  const relevantAssets = relevantPeriodPriceSeries(data);
  const pricePointsByAssetId = new Map<number, AssessmentChartPoint[]>();
  const yieldPointsByAssetId = new Map<number, AssessmentChartPoint[]>();

  relevantAssets.forEach(asset => {
    pricePointsByAssetId.set(
      asset.underlying_asset_id,
      asset.points.map(point => ({
        x: point.timestamp,
        y: pmChartPrice(point, asset.is_fx_forward),
      })),
    );
    if (asset.is_bond) {
      yieldPointsByAssetId.set(
        asset.underlying_asset_id,
        asset.points.map(point => ({
          x: point.timestamp,
          y: finiteOrNull(point.ask_yield),
        })),
      );
    }
  });

  const startAssetId =
    data?.start_underlying_asset_id ?? relevantAssets[0]?.underlying_asset_id;
  const endAssetId =
    data?.end_underlying_asset_id ??
    relevantAssets[relevantAssets.length - 1]?.underlying_asset_id;
  const priceBoundary = boundaryValues(
    startAssetId === undefined
      ? undefined
      : pricePointsByAssetId.get(startAssetId),
    endAssetId === undefined ? undefined : pricePointsByAssetId.get(endAssetId),
  );
  const yieldBoundary = boundaryValues(
    startAssetId === undefined
      ? undefined
      : yieldPointsByAssetId.get(startAssetId),
    endAssetId === undefined ? undefined : yieldPointsByAssetId.get(endAssetId),
  );

  return {
    pricePercentage:
      priceBoundary && priceBoundary.start !== 0
        ? ((priceBoundary.end - priceBoundary.start) /
            Math.abs(priceBoundary.start)) *
          100
        : undefined,
    yieldBasisPoints: yieldBoundary
      ? (yieldBoundary.end - yieldBoundary.start) * 100
      : undefined,
  };
};
